<template>
  <v-dialog v-model="dialog" width="915" persistent>
    <v-card class="trans-detail-card">
      <v-card-title>
        <span>{{ $t('amazon_transparency_transaction_detail') }}</span>
      </v-card-title>

      <!-- modal body -->
      <div class="ma-5">
        <!-- first row  -->
        <div class="d-flex align-center" v-if="selectedTransaction?.source">
          <span class="text-bold font-weight-bold w-120">{{ $t('amazon_transparency_source') }}</span>
          <span class="w-120 text-capitalize">{{ selectedTransaction.source }}</span>
<!--          <span class="text-decoration-underline text-underline view-link">wo_id - WO ref</span>-->
        </div>

          <!-- second row  -->
        <div class="mt-3 d-flex align-center mb-5">
          <span class="text-bold font-weight-bold w-120" v-if="selectedTransaction?.created_at">{{ $t('amazon_transparency_date_created') }}</span>
          <span class="text-light" v-if="selectedTransaction?.created_at">{{ selectedTransaction.created_at }}</span>
          <v-spacer></v-spacer>
          <div class="d-flex align-center">
            <v-icon color="black" size="24" class="align-self-center mr-5">mdi-filter-variant</v-icon>
            <v-select
              v-model="transactionStatus"
              :items="statusFilterOptions"
              :item-disabled="isItemDisabled"
              label="Status"
              dense
              solo
              height="40"
              class="custom-select elevation-0"/>
          </div>
        </div>

        <!-- table -->
        <div class="mt-5">
          <v-data-table
            :headers="headers"
            :items-per-page="5"
            :items="files"
            :loading="isLoadingFiles"
            :server-items-length="totalFiles"
            :footer-props="{
              'items-per-page-text': '',
              'disable-items-per-page': true
      }"
            class="tbya-transactions-table"
            item-value="id"
            :loading-text="$t('amazon_transparency_loading_transactions')"
            hide-default-footer
          >

            <!-- source file -->
            <template v-slot:item.source_file="{ item }">
              <span
                v-if="item?.source_file"
                class="view-link text-capitalize text-decoration-underline text-view"
                @click="openLink(item, 'source_file')"
              >
                {{ $t('amazon_transparency_view')}}
              </span>
            </template>

            <!-- error log -->
            <template v-slot:item.error_log="{ item }">
              <span
                v-if="item?.error_log"
                class="view-link text-capitalize text-decoration-underline text-view"
                @click="openLink(item, 'error_log')"
              >
                {{ $t('amazon_transparency_view')}}
              </span>
            </template>

            <!-- status -->
            <template v-slot:item.status="{ item }">
              <span class="text-capitalize" :class="utils.getStatusStyle(item.status.toLowerCase())">
                {{ utils.transformStatusText(item.status) }}
              </span>
            </template>

            <!-- table footer  -->
            <template v-slot:footer>
              <div class="d-flex justify-end mt-5 mb-4">
                <v-pagination
                  class="flex-grow-0"
                  v-model="options.page"
                  :length="totalPages"
                  :total-visible="7"
                  @click.stop
                />
              </div>
            </template>
          </v-data-table>
        </div>
      </div>

      <!-- modal actions -->
      <v-card-actions class="pa-4 px-8 mt-3 pb-6">
        <v-spacer></v-spacer>
        <v-btn class="text-uppercase mr-4" text color="primary" @click="closeModal">
          {{ $t('amazon_transparency_close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import FilesService from '@/services/atp-files'
import Utils from '@/utils/utils'

export default {
  name: 'TransactionDetailDialog',
  props: {
    displayDetailDialog: {
      type: Boolean,
      required: true
    },
    transaction: {
      type: [Object, null],
      required: false,
      validator (value) {
        return (
          value === null || value instanceof Object
        )
      }
    }
  },
  data () {
    return {
      transactionStatus: 'status',
      selectedTransaction: this.transaction,
      statusFilterOptions: [
        { text: this.$t('amazon_transparency_status_header'), value: null, disabled: true },
        { text: this.$t('amazon_transparency_all'), value: 'all' },
        { text: this.$t('amazon_transparency_pending'), value: 'pending' },
        { text: this.$t('amazon_transparency_failed'), value: 'failed' },
        { text: this.$t('amazon_transparency_completed'), value: 'success' }
      ],
      headers: [
        { text: this.$t('amazon_transparency_connected_codes'), value: 'active_codes', sortable: false, align: 'center' },
        { text: this.$t('amazon_transparency_status_header'), value: 'status', sortable: false, align: 'center' },
        { text: this.$t('amazon_transparency_submitted_at'), value: 'submitted_at', align: 'center', sortable: false },
        { text: this.$t('amazon_transparency_source_file'), value: 'source_file', sortable: false, align: 'center' },
        { text: this.$t('amazon_transparency_error_log'), value: 'error_log', sortable: false, align: 'center' }
      ],
      defaultParams: { ordering: '-created_at', limit: 5 },
      options: {
        page: 1,
        itemsPerPage: 5
      }
    }
  },
  methods: {
    closeModal () {
      this.selectedTransaction = null
      this.dialog = false
    },
    getUrlParams (extraParams) {
      const merged = { ...this.defaultParams, ...extraParams }
      const params = new URLSearchParams()

      for (const key in merged) {
        if (merged[key] != null && merged[key] !== '') {
          params.append(key, merged[key])
        }
      }

      return params
    },
    async fetchTransactionFiles () {
      try {
        const { page = 1, itemsPerPage = 5 } = this.options
        const extraParams = {
          offset: (page - 1) * itemsPerPage,
          limit: itemsPerPage,
          transaction: this.selectedTransaction?.id
        }

        if (this.transactionStatus.toLowerCase() !== 'all' && this.transactionStatus.toLowerCase() !== 'status') {
          extraParams.status = this.transactionStatus.toLowerCase() === 'failed' ? 'error' : this.transactionStatus.toLowerCase() === 'success' ? 'ok'
            : this.transactionStatus.toLowerCase() === 'pending' ? 'submitted' : this.transactionStatus
        }

        const payload = this.getUrlParams(extraParams)
        await this.$store.dispatch('setIsLoadingFiles', true)
        await this.$store.dispatch('loadFiles', payload)
      } catch (err) {
        console.log('error', err)
      } finally {
        await this.$store.dispatch('setIsLoadingFiles', false)
      }
    },
    openLink (item, type) {
      if (type === 'source_file') {
        FilesService.downloadFile(item?.source_file, item?.capture_id, 'json')
      } else {
        FilesService.downloadFile(item?.error_log, item?.capture_id, 'csv')
      }
    },
    isItemDisabled (item) {
      return item.disabled
    }
  },
  computed: {
    ...mapGetters(['files', 'isLoadingFiles', 'totalFiles']),
    dialog: {
      get () {
        return this.displayDetailDialog
      },
      set (value) {
        this.$emit('update:displayDetailDialog', value)
      }
    },
    disableSelect () {
      return this.totalFiles === 0
    },
    utils () {
      return Utils
    },
    totalPages () {
      return Math.ceil(this.totalFiles / this.options.itemsPerPage)
    }
  },
  watch: {
    transaction: {
      handler (newVal) {
        this.selectedTransaction = newVal
        if (newVal !== null) {
          newVal && this.fetchTransactionFiles()
        }
      },
      immediate: true
    },
    transactionStatus () {
      this.fetchTransactionFiles()
    }
  }
}
</script>

<style lang="sass">
  @mixin text-styles($color)
    color: $color
    font-size: 14px
    font-style: normal
    font-weight: 400
    line-height: 20px
    letter-spacing: 0.25px

  .trans-detail-card
    width: 100% !important
    max-width: 100% !important
    overflow-x: hidden !important

    .w-120
      width: 120px !important

    .text-bold
      color: #000
      font-size: 16px
      font-style: normal
      font-weight: 700
      line-height: 26px

    .text-light
      color: #000
      font-size: 16px
      font-style: normal
      font-weight: 400
      line-height: 26px

    .text-underline
      font-size: 16px !important
      font-style: normal !important
      font-weight: 400 !important
      line-height: 26px !important
      text-decoration-line: underline !important
      text-decoration-style: solid !important
      text-underline-color: #15BBF0 !important

    .view-link
      color: #15BBF0 !important
      cursor: pointer !important
      &:hover
        color: #15BBF0 !important

    .text-view
      font-size: 14px !important
      font-style: normal !important
      font-weight: 400 !important
      line-height: 20px !important
      letter-spacing: 0.25px !important
</style>
